/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-await-in-loop */
/* eslint no-irregular-whitespace: ["error", { "skipStrings": true }] */
import axios, { CancelTokenSource } from 'axios'

import ContratNormalizer from 'normalizer/ContratNormalizer'
import { distinctArray } from 'utils/Common/array-utils'
import regroupURL from 'utils/Perimeter/ContratUtils'
import ContratEntity from 'utils/__interfaces/Perimeter/ContratEntity'
import ROUTES from 'utils/ROUTES'
import OrganizationEntity from 'utils/__interfaces/Administrations/Organizations/OrganizationEntity'
import { TIME_OUT_TABLE } from 'utils/__consts/Common/Table'
import isCancel from 'utils/Common/api-utils'
import {
  getMemberData,
  getNextPage,
  getTotalItems,
  isLastPage,
} from './hydra-services'

let cancelToken: CancelTokenSource

export interface SearchResult {
  entities: ContratEntity[]
  totalItems: number
}

async function searchContrat(
  url: string,
  history: any,
  maxTotalLimit = -1,
  pagination = false,
  byPassCancel = true,
  params?: URLSearchParams
): Promise<SearchResult> {
  let currentUrl = url
  let result: ContratEntity[] = []
  let totalItems = 0
  let data

  if (!byPassCancel) {
    if (cancelToken !== undefined) {
      cancelToken.cancel('Operation canceled due to new request.')
    }

    cancelToken = axios.CancelToken.source()
  }

  try {
    let hasMorePage = true
    while (hasMorePage) {
      const resp = byPassCancel
        ? await axios({
            method: 'GET',
            url: currentUrl,
            params,
          })
        : await axios({
            method: 'GET',
            url: currentUrl,
            params,
            cancelToken: cancelToken?.token,
          })

      data = getMemberData(resp.data)
      totalItems = getTotalItems(resp.data)
      const entities = ContratNormalizer.normalize(data)
      result = [...result, ...entities]

      if (
        data.length === 0 ||
        (maxTotalLimit > -1 && totalItems > maxTotalLimit)
      ) {
        hasMorePage = false
      } else if (data.length > 0) {
        hasMorePage = !isLastPage(resp.data) && !pagination
        currentUrl = `${process.env.REACT_APP_BASE_API_WDH_URL}${getNextPage(
          resp.data
        )}`
      }
    }
  } catch (err) {
    if (isCancel(err)) {
      return Promise.resolve({ totalItems: 0, entities: err })
    }
    history.push(ROUTES.TechnicalError)
    throw new Error(
      `Erreur lors de l'exécution de l'api searchContrat avec l'url: ${url}`
    )
  }

  return {
    entities: result,
    totalItems,
  }
}

export async function searchContratByTextAndOrganizationsIds(
  value: string,
  organizationIds: string[],
  history: any,
  maxTotalLimit = -1,
  disablePerimeter?: boolean,
  specificPerPage?: number,
  expandedMode = false
): Promise<SearchResult> {
  let url
  const params = new URLSearchParams({})

  if (value) {
    params.append('searchText', value)
  }

  if (specificPerPage) {
    url = `${process.env.REACT_APP_BASE_API_WDH_URL}/contrats?perPage=${specificPerPage}`
  } else {
    url = `${process.env.REACT_APP_BASE_API_WDH_URL}/contrats?perPage=${process.env.REACT_APP_BASE_API_WDH_MAX_RESULT}`
  }

  if (expandedMode) url += '&expandedMode=true'

  if (!disablePerimeter) {
    organizationIds.forEach((id) => {
      params.append('idOrganisation[]', id)
    })
  }

  return searchContrat(url, history, maxTotalLimit, false, true, params)
}

export function searchContratByOrganizationIds(
  organizationIds: string[],
  history: any,
  expandedMode = false
): Promise<SearchResult> {
  if (!organizationIds) {
    return Promise.resolve({
      entities: [],
      totalItems: 0,
    })
  }

  let url = `${process.env.REACT_APP_BASE_API_WDH_URL}/contrats?perPage=${process.env.REACT_APP_BASE_API_WDH_MAX_RESULT}&sort[idFonctionnelContrat]=asc`
  if (expandedMode) url += '&expandedMode=true'

  const params = new URLSearchParams({})
  organizationIds.forEach((id) => {
    params.append('idOrganisation[]', id)
  })

  return searchContrat(url, history, -1, false, true, params)
}

async function responsesAnalyser(responses: any[]): Promise<SearchResult> {
  let entity: ContratEntity[] = []
  const result = {
    entities: entity,
    totalItems: 0,
  }

  responses.forEach((resp: any) => {
    if (resp.data) {
      const data = getMemberData(resp.data)
      const totalItems = getTotalItems(resp.data)

      const entities = ContratNormalizer.normalize(data)

      entity = entity.concat(entities)
      result.totalItems += totalItems
    }
  })

  result.entities = entity
  return Promise.resolve(result)
}

export async function searchContratByContratIds(
  contratIds: string[],
  history: any
): Promise<SearchResult> {
  if (!contratIds) {
    return {
      entities: [],
      totalItems: 0,
    }
  }

  let url = `${process.env.REACT_APP_BASE_API_WDH_URL}/contrats?perPage=${process.env.REACT_APP_BASE_API_WDH_MAX_RESULT}`
  const distinctContratIds = distinctArray(contratIds)
  const maxValue =
    parseInt(`${process.env.REACT_APP_MAX_COMPANY_ID_LENGTH}`, 10) || 200
  const entity: ContratEntity[] = []
  let result = {
    entities: entity,
    totalItems: 0,
  }

  if (distinctContratIds.length < maxValue) {
    distinctContratIds.forEach((id) => {
      url = url.concat(`&idContrat[]=${id}`)
    })
    return searchContrat(url, history)
  }

  const urlArray = regroupURL(distinctContratIds, url, maxValue)

  const requestArray: any[] = []
  urlArray.forEach((uri: string) => {
    requestArray.push(axios.get(uri))
  })

  const promisesResolved = requestArray.map((request) =>
    request.catch((err: any) => ({ err }))
  )

  await axios
    .all(promisesResolved)
    .then(
      axios.spread(async (...responses: any[]) => {
        result = await responsesAnalyser(responses)
      })
    )
    .catch((err: any) => {
      console.error(err)
      history.push(ROUTES.TechnicalError)
    })

  return result
}

export async function getContratById(
  contratId: string,
  history: any
): Promise<ContratEntity> {
  let entity = {
    id: '',
    name: '',
    address: '',
    siret: '',
    villeCP: '',
    shortAdress: '',
    numeroContratSAP: '',
    numDebiteur: '',
  }

  const url = `${process.env.REACT_APP_BASE_API_WDH_URL}/contrats/${contratId}`

  if (cancelToken !== undefined) {
    cancelToken.cancel('Operation canceled due to new request.')
  }

  cancelToken = axios.CancelToken?.source()

  try {
    const resp = await axios({
      method: 'GET',
      url,
      cancelToken: cancelToken?.token,
    })

    if (resp.data) {
      const countainer = []
      countainer.push(resp.data)
      const entities = ContratNormalizer.normalize(countainer)
      const [result] = entities
      entity = result
    }
  } catch (e) {
    if (isCancel(e)) {
      return e
    }
    console.error(e)
    history.push(ROUTES.TechnicalError)
    return entity
  }

  return entity
}

export async function getContratsByIds(
  contratIds: string[],
  history: any,
  byPassCancel: boolean
): Promise<SearchResult> {
  if (!contratIds || contratIds.length === 0) {
    return {
      entities: [],
      totalItems: 0,
    }
  }

  let url = `${process.env.REACT_APP_BASE_API_WDH_URL}/contrats?`

  contratIds.forEach((id) => {
    url = url.concat(`&idContrat[]=${id}`)
  })

  return searchContrat(url, history, -1, false, byPassCancel)
}

export async function searchContratByOrgaAndContrat(
  contratIds: string[],
  organisationId: string,
  history: any,
  page?: number,
  nbPage?: number,
  expandedMode = false
): Promise<SearchResult> {
  if (!contratIds || !organisationId) {
    return {
      entities: [],
      totalItems: 0,
    }
  }

  const nb = nbPage
    ? `perPage=${nbPage}`
    : `perPage=${process.env.REACT_APP_BASE_API_WDH_MAX_RESULT}`
  const p = page ? `&page=${page}` : ''

  let url = `${process.env.REACT_APP_BASE_API_WDH_URL}/organisations/${organisationId}/contrats?${nb}${p}&sort[idFonctionnelContrat]=asc`

  contratIds.forEach((id) => {
    url = url.concat(`&idContrat[]=${id}`)
  })

  if (expandedMode) url = url.concat('&expandedMode=true')
  const pagination = !!nbPage

  return searchContrat(url, history, -1, pagination)
}

export async function getContratByIdOrganization(
  idOrganization: string,
  history: any,
  page?: number,
  nbPage?: number,
  contrat: { id: string }[] = [],
  expandedMode = false
): Promise<SearchResult> {
  const params = new URLSearchParams()
  const nb = nbPage
    ? `perPage=${nbPage}`
    : `perPage=${process.env.REACT_APP_BASE_API_WDH_MAX_RESULT}`
  const p = page ? `&page=${page}` : ''

  let url = `${process.env.REACT_APP_BASE_API_WDH_URL}/organisations/${idOrganization}/contrats?${nb}${p}&sort[idFonctionnelContrat]=asc`
  let result: ContratEntity[] = []
  let totalItems = 0
  let data

  contrat.forEach((ctr) => {
    params.append(`idContrat[]`, ctr.id)
  })

  if (expandedMode) params.append(`expandedMode`, 'true')

  try {
    let hasMorePage = true
    while (hasMorePage) {
      const resp = await axios({
        method: 'GET',
        url,
        params,
      })

      data = getMemberData(resp.data)
      totalItems = getTotalItems(resp.data)

      if (data.length > 0) {
        const entities = ContratNormalizer.normalize(data)
        result = [...result, ...entities]

        hasMorePage = !isLastPage(resp.data) && !nbPage
        url = `${process.env.REACT_APP_BASE_API_WDH_URL}${getNextPage(
          resp.data
        )}`
      } else {
        hasMorePage = false
      }
    }
  } catch (err) {
    history.push(ROUTES.TechnicalError)
    throw new Error(
      `Erruer lors de l'exécution de l'api getContratByIdOrganization avec l'url: ${url}`
    )
  }

  return {
    entities: result,
    totalItems,
  }
}

export async function getContratsForTable(
  perPage: number,
  page: number,
  organisation: OrganizationEntity[] = [],
  contrat: { id: string }[] = [],
  filters: { [key: string]: string[] },
  history: any
): Promise<{
  results: ContratEntity[]
  pageCount: number
  totalItems: number
  isTimeOut: boolean
}> {
  const url = `${process.env.REACT_APP_BASE_API_WDH_URL}/contrats`
  let listOfContrat: ContratEntity[] = []
  const params = new URLSearchParams()

  params.append(`page`, String(page))
  params.append(`perPage`, String(perPage))

  organisation.forEach((org) => {
    params.append(`idOrganisation[]`, org.id)
  })

  contrat.forEach((ctr) => {
    params.append(`idContrat[]`, ctr.id)
  })

  params.append('sort[idFonctionnelContrat]', 'asc')

  Object.entries(filters).forEach(([key, values]) => {
    values.forEach((value: string) => params.append(`${key}[]`, value))
  })

  if (cancelToken !== undefined) {
    cancelToken.cancel('Operation canceled due to new request.')
  }

  cancelToken = axios.CancelToken.source()

  try {
    const resp = await axios({
      method: 'GET',
      url,
      params,
      cancelToken: cancelToken.token,
      timeout: TIME_OUT_TABLE,
    })

    if (resp?.data) {
      listOfContrat = ContratNormalizer.normalize(getMemberData(resp.data))
      const totalItems = parseInt(resp.data['hydra:totalItems'], 10)

      return {
        results: [...listOfContrat],
        pageCount: Math.ceil(totalItems / perPage),
        totalItems,
        isTimeOut: false,
      }
    }
  } catch (err) {
    if (err?.code === 'ECONNABORTED') {
      return Promise.resolve({
        pageCount: 0,
        results: [],
        totalItems: 0,
        isTimeOut: true,
      })
    }

    if (isCancel(err)) {
      return Promise.resolve({
        pageCount: 0,
        results: err,
        totalItems: 0,
        isTimeOut: false,
      })
    }

    history.push(ROUTES.TechnicalError)
    throw new Error(`Erreur : ${err}`)
  }

  return Promise.resolve({
    pageCount: 0,
    results: [],
    totalItems: 0,
    isTimeOut: false,
  })
}
